import type { ColumnDef } from "@tanstack/react-table";
import { capitalize, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { Table } from "~/providers/table-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useTable } from "~/hooks/use-table";
import { useTableManager } from "~/managers/use-table-manager";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_main/settings/_layout/tables")({
  component: Tables,
});

function Tables() {
  const { t } = useTranslation("common");
  const { tables } = useTable();
  const { openTableDialog } = useTableManager();
  const columns: ColumnDef<Table>[] = [
    {
      id: "bookable",
      accessorKey: "bookable",
      filterFn: "includesString",
      size: 10,
      header: ({}) => null,
      cell: ({ row: { original: table } }) => {
        return (
          <div
            className={cn(
              "w-4 h-4 rounded-full",
              table.bookable && "bg-green-500",
              !table.bookable && "bg-red-500",
            )}
          ></div>
        );
      },
    },
    {
      id: t("name"),
      accessorKey: "reference",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: table }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(table.reference, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("Table.seats"),
      accessorKey: "seats",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Table.seats")} />
      ),
      cell: ({ row: { original: table }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  `${table.minSeats.toString()} - ${table.maxSeats.toString()}`,
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("Table.position"),
      accessorKey: "position",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Table.position")} />
      ),
      cell: ({ row: { original: table }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(table.position.toString(), globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },

    {
      id: t("Table.section"),
      accessorKey: "locationName",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Table.section")} />
      ),
      cell: ({ row: { original: table }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(table.locationName, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
  ];
  return (
    <>
      <SettingsHeader
        title={t("tables")}
        buttonAction={async () => {
          await openTableDialog();
        }}
        buttonLabel={capitalize(t("new_table"))}
      />

      <DataTable
        defaultSorting={[{ id: t("Table.position"), desc: false }]}
        columns={columns}
        data={tables}
        onSelect={openTableDialog}
      />
    </>
  );
}
